import {
  Mic,
  BellDot,
  Calendar,
  History,
  ListChecks,
  MessageSquareText,
  Send,
  Sparkles,
  UserStar,
  type LucideIcon,
} from 'lucide-react'
import { MEETING_VIEW_ID } from './components/meeting/meeting-view-id'
import { getResolvedMeetingIds } from './views/meeting-view/meeting-resolution'
import type { ChatOutputViewContext } from './views/chat-output-view/use-chat-output-view-model'
import type { MeetingViewContext } from './views/meeting-view/use-meeting-view-model'
import type { ActionViewContext } from './views/action-view/use-action-view-model'
import type { ComposerDraft } from './views/chat-view/use-composer-draft'

/**
 * What each View is, without what it renders: its context type, its navigation
 * identity, and the labels the chrome names it by. Nothing here imports a
 * component, so `view-atoms`, the cells that call it, and the popout route can
 * resolve a view's identity without pulling every View into their module graph,
 * and a hot edit to a View never invalidates the atoms that hold the history.
 * `view-registry.tsx` pairs each id here with its components.
 */
interface ViewDefinitionConfig<TContext> {
  /** Label for the back/forward nav tooltips. A function form may consult the open context. */
  label?: string | ((context: TContext) => string)
  /**
   * Icon for this view in the breadcrumb trail menus. Required so a new view
   * can't reach the trail without one — the nav button used to keep its own
   * id→icon map, which silently fell back to a chat bubble for anything added
   * here.
   */
  icon: LucideIcon
  /** Placeholder the composer shows while this view is on top. */
  chatInputPlaceholder?: string
  /**
   * Name for *this* open document in the breadcrumb trail, where `label`'s
   * per-view-kind wording ("Chat", "Meeting") is useless in a list of three
   * chats. Views that show one of many things declare how to title themselves;
   * everything else falls back to `label`.
   */
  trailLabel?: (context: TContext) => string | undefined
  /**
   * Keep the chat input full-width on this view. Full-list views read as an
   * extension of Home, so the input stays put; detail views inset it to sit
   * over the surface (per the View designs).
   */
  fullWidthChatInput?: boolean
  /**
   * Which *document* an open view is showing, for the navigation dedupe in
   * `openViewInHistory` and for keying its popout window. Views that show one
   * of many things (a chat, a reminder) must supply this, or opening a second
   * one would collapse onto the first. Omit for single-destination views — one
   * Actions list is every Actions list.
   *
   * Returns `string`, never `string | undefined`: an undefined identity used
   * to be interpolated into the dedupe key as the literal `"undefined"`, so
   * two *different* documents that hadn't resolved their ids yet collapsed
   * onto one entry and inherited each other's scroll and draft. Each view
   * names its own no-id bucket instead.
   */
  identity?: (context: TContext) => string
  /**
   * Enrich an *incoming* open's context before it's pushed (and before
   * `identity` reads it) — for filling in ids the opener didn't know but the
   * session already resolved, so an open by one of a document's names lands
   * on the entry keyed by another. Runs once at the moment of intent;
   * mounted entries are never rewritten by it.
   */
  normalize?: (context: TContext) => TContext
  /**
   * The context an open lands with when it collapses onto this view's entry
   * (see `openViewInHistory`). Omitted, the incoming open's context replaces
   * the entry's. Declare it to keep state the user made on the entry, which
   * no opener knows about.
   */
  reopen?: (current: TContext, incoming: TContext) => TContext
}

function defineView<TContext = void>(config: ViewDefinitionConfig<TContext>) {
  return {
    // Phantom: never read at runtime. It exists so `ViewContexts` can recover
    // TContext off the definition (`(typeof viewDefinitions)[K]['context']`) —
    // TContext appears in contravariant position (`identity`, `trailLabel`),
    // so it can't be recovered from those without widening every consumer to
    // `unknown`.
    context: undefined as TContext,
    ...config,
    fullWidthChatInput: config.fullWidthChatInput ?? false,
  }
}

export const viewDefinitions = {
  chat: defineView<{
    chatId?: string
    title?: string
    isNew?: boolean
    instantScrollToBottom?: boolean
    /**
     * Scroll to (and surface) a specific message once the conversation loads —
     * used to jump straight to a background AskUserQuestion in its chat context.
     */
    scrollToMessageId?: string
    /**
     * Changes on every open so reopening the same question (same id, mounted
     * view) still re-triggers the scroll. See getQuestionChatTarget.
     */
    scrollNonce?: number
    /**
     * When this chat was opened for a background question, the question's staged
     * action id. If the conversation can't be loaded, the view falls back to
     * the Action View for this action. See getQuestionChatTarget.
     */
    questionActionId?: string
    /**
     * Set when opened by a fresh local submit that already owns an in-flight
     * stream (e.g. a brief submit). Suppresses the streaming re-subscribe.
     */
    isFreshSubmit?: boolean
    /**
     * Whether a pending AskUserQuestion/AskUserFeedback is waiting on the user
     * in this conversation. Mirrored from the staged-actions cache by the view
     * — see ChatView.
     */
    hasPendingQuestions?: boolean
    /** Keys a new chat opened to send `initialPrompt`, so each send gets its own window. */
    draftId?: string
    /** Sent once by the view when it mounts as a new chat. */
    initialPrompt?: string
    /** The model `initialPrompt` goes to. Its text @-mentions it. */
    initialPromptModelId?: string
    /** The skill `initialPrompt` invokes. Its text names it after a `/`. */
    initialPromptSkillId?: string
    /** Sends `initialPrompt` with what's attached to the composer, so the new-chat reset leaves it in place. */
    sendsComposerAttachments?: boolean
    /** Left in the composer, unsent, while the view is a new chat. */
    composerDraft?: ComposerDraft
  }>({
    label: 'Chat',
    icon: MessageSquareText,
    trailLabel: (context) => context?.title,
    // A chat with no id yet is the "New Chat" draft — one of those at a time,
    // but distinct from every loaded conversation.
    identity: (context) => context?.chatId ?? context?.draftId ?? 'new',
  }),
  // Prototype: latest completed turn without the full thread. Opened from
  // Home's Running Chats rows only — other entry points still open Chat View.
  chatOutput: defineView<ChatOutputViewContext>({
    label: 'Output',
    icon: MessageSquareText,
    chatInputPlaceholder: 'Ask Highlight to make changes…',
    trailLabel: (context) => context?.title,
    identity: (context) => context?.conversationId ?? 'none',
  }),
  // One View for a meeting's whole lifecycle (HIG-5823) — live transcription,
  // ended, generating, finalized. `context.mode` says whether this entry
  // follows the running recording or names a specific meeting.
  [MEETING_VIEW_ID]: defineView<MeetingViewContext>({
    label: 'Meeting',
    icon: Mic,
    chatInputPlaceholder: 'Ask Highlight about this meeting…',
    trailLabel: (context) => context?.title,
    // Calendar event first: it outlives the recording, so a scheduled meeting
    // keeps one identity while live and again once saved, instead of stacking a
    // second Meeting View for the same meeting. Mirrors `meetingNotepadKey`.
    // An open with neither id is the live meeting; `useMeetingContextLatch`
    // fills them in as the view resolves them, so it stops being a wildcard —
    // and a `note` open of a meeting already open `live` lands on that entry.
    identity: (context) => context?.calendarEventId ?? context?.audioNoteId ?? 'live',
    // The latch heals *entries*, but an incoming open only knows what its
    // opener knew — Mini's "View" and the history rows open by note id alone,
    // which reads as a different document from the same meeting keyed by its
    // calendar event, and stacks a second Meeting View (the mid-meeting →
    // preparing/finished handoff). Fill the missing ids from the session's
    // meeting index, which knows the note ↔ event join for exactly the
    // meetings a colliding entry could exist for.
    normalize: (context) => {
      if (!context || (context.audioNoteId && context.calendarEventId)) return context
      const resolved = getResolvedMeetingIds({
        audioNoteId: context.audioNoteId,
        calendarEventId: context.calendarEventId,
      })
      if (resolved.audioNoteId === context.audioNoteId && resolved.calendarEventId === context.calendarEventId) {
        return context
      }

      return {
        ...context,
        ...(resolved.audioNoteId ? { audioNoteId: resolved.audioNoteId } : {}),
        ...(resolved.calendarEventId ? { calendarEventId: resolved.calendarEventId } : {}),
      }
    },
    // The chat thread is the entry's, not the opener's. The save-time open (by
    // note id) lands here as the meeting ends, and must not drop the thread the
    // user is in. An opener asking for a tab is asking to leave the thread.
    reopen: (current, incoming) => ({
      ...incoming,
      chatId: incoming.chatId ?? current?.chatId,
      newChatId: incoming.newChatId ?? current?.newChatId,
      ...(incoming.initialTab ? {} : { chat: current?.chat, isFreshSubmit: current?.isFreshSubmit }),
    }),
  }),
  // The native Action View (HIG-5824).
  action: defineView<ActionViewContext>({
    // A pending AskUserQuestion is framed as a question, and a pending
    // AskUserFeedback as feedback — neither is an action the user is approving.
    label: (context) =>
      context?.kind === 'question' ? 'Question' : context?.kind === 'feedback' ? 'Feedback' : 'Action',
    icon: Sparkles,
    // `actionId` is required on the context, so the fallback only catches an
    // open with no context at all — a malformed deeplink. One bucket for those,
    // rather than a key that reads as a real id. The chat drawer (HIG-5977) is
    // an overlay on this same entry, not its own document, so `chat` stays out
    // of the identity.
    identity: (context) => context?.actionId ?? 'none',
  }),
  // The native History View (HIG-5836).
  history: defineView({
    label: 'History',
    icon: History,
    fullWidthChatInput: true,
  }),
  forYou: defineView<{ initialFilter?: 'all' | 'staged' | 'completed' | 'archived' }>({
    label: 'For You',
    icon: UserStar,
    fullWidthChatInput: true,
  }),
  // Label and icon track the view's own header, so the breadcrumb trail names it
  // the way the view does (Figma 7652:22276).
  actions: defineView({
    label: 'Ready for you',
    icon: Send,
    fullWidthChatInput: true,
  }),
  upcoming: defineView({
    label: 'Upcoming',
    icon: Calendar,
    fullWidthChatInput: true,
  }),
  reminders: defineView<{ initialTab?: 'active' | 'pinned' | 'resolved'; focusReminderId?: string }>({
    label: 'Reminders',
    icon: BellDot,
    fullWidthChatInput: true,
  }),
  // The Reminder detail View.
  reminder: defineView<{ reminderId: string }>({
    label: 'Reminder',
    icon: ListChecks,
    // `reminderId` is required on the context — see the Action View's note.
    identity: (context) => context?.reminderId ?? 'none',
  }),
} as const

/** Every id the registry can render. Home is not a View — it renders outside the router. */
export type ViewId = keyof typeof viewDefinitions

export type ViewContexts = {
  [K in ViewId]: (typeof viewDefinitions)[K]['context']
}

/** The shared shape every definition satisfies, with contexts widened to `unknown`. */
export type ViewDefinition = ReturnType<typeof defineView<unknown>>

/** An open view, as everything outside the registry holds one. */
export interface OpenView {
  id: string
  context?: unknown
}

// The cast widens each entry's typed context callbacks to `unknown`; callers
// only ever hand back the context the same entry produced.
export const viewDefinition = (id: string): ViewDefinition | undefined =>
  viewDefinitions[id as ViewId] as ViewDefinition | undefined

export function isViewId(id: string): id is ViewId {
  return Object.prototype.hasOwnProperty.call(viewDefinitions, id)
}

/**
 * Which document an open view is showing, the navigation dedupe key. Views
 * without an `identity` rule have a single destination, so the id alone is it.
 */
export function viewIdentity(view: OpenView): string {
  const identify = viewDefinition(view.id)?.identity
  return identify ? `${view.id}:${identify(view.context)}` : view.id
}

/** The view an open lands as when it collapses onto `current`, per the view's `reopen` rule (if any). */
export function reopenView<V extends OpenView>(current: V, incoming: V): V {
  const reopen = viewDefinition(incoming.id)?.reopen
  return reopen && current.id === incoming.id
    ? { ...incoming, context: reopen(current.context, incoming.context) }
    : incoming
}

/** An incoming open's context, enriched by the view's `normalize` rule (if any). */
export function normalizeViewContext(id: string, context: unknown): unknown {
  const normalize = viewDefinition(id)?.normalize
  return normalize && context !== undefined ? normalize(context) : context
}

/** Whether opening this view without a context is a type error. */
export type ViewRequiresContext<K extends ViewId> = ViewContexts[K] extends void | undefined
  ? false
  : object extends ViewContexts[K]
    ? false
    : true

/**
 * Read an open view's context *as* a given view's, or undefined when that
 * isn't the view on top.
 *
 * The alternative — `topView.context as { chatId?: string }` at each call site —
 * re-declares a shape the registry already owns, so renaming a context field
 * silently breaks nothing and fixes nothing. This is the one place that cast
 * lives, and it's guarded by the id check right above it.
 */
export function viewContextOf<K extends ViewId>(view: OpenView, id: K): ViewContexts[K] | undefined {
  return view.id === id ? (view.context as ViewContexts[K] | undefined) : undefined
}
